import { supabase } from '../supabaseClient';

const SUPABASE_URL = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/$/, '');
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY || '';

async function getAccessToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || '';
}

async function parseJsonSafe(res) {
  try {
    return await res.json();
  } catch (_) {
    return null;
  }
}

function toPostgrestError(payload, fallbackMessage) {
  if (payload && typeof payload === 'object' && (payload.message || payload.code)) {
    return {
      message: payload.message || fallbackMessage || 'Request failed',
      code: payload.code || null,
      details: payload.details || null,
      hint: payload.hint || null,
    };
  }
  return { message: fallbackMessage || 'Request failed', code: null, details: null, hint: null };
}

async function rpcFetch(fnName, params, method) {
  const token = await getAccessToken();
  const headers = {
    apikey: SUPABASE_KEY,
    Authorization: token ? `Bearer ${token}` : `Bearer ${SUPABASE_KEY}`,
    'Accept-Profile': 'public',
    'Content-Profile': 'public',
    Accept: 'application/json',
  };

  const base = `${SUPABASE_URL}/rest/v1/rpc/${encodeURIComponent(fnName)}`;
  if (method === 'GET') {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined) return;
      qs.set(k, v === null ? 'null' : String(v));
    });
    const url = qs.toString() ? `${base}?${qs.toString()}` : base;
    const res = await fetch(url, { method: 'GET', headers });
    const payload = await parseJsonSafe(res);
    if (!res.ok) {
      return { data: null, error: toPostgrestError(payload, `RPC ${fnName} failed`), status: res.status };
    }
    return { data: payload, error: null, status: res.status };
  }

  const res = await fetch(base, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json', Prefer: 'return=representation' },
    body: JSON.stringify(params || {}),
  });
  const payload = await parseJsonSafe(res);
  if (!res.ok) {
    return { data: null, error: toPostgrestError(payload, `RPC ${fnName} failed`), status: res.status };
  }
  return { data: payload, error: null, status: res.status };
}

async function rpcResilient(fnName, params) {
  // Prefer POST for volatile RPCs; some Supabase gateways sometimes return a 404 for POST
  // during schema-cache mismatch, while GET still works. Fall back to GET on 404.
  const post = await rpcFetch(fnName, params, 'POST');
  if (post.status !== 404) return post;
  return rpcFetch(fnName, params, 'GET');
}

export async function getMyCoachAccount() {
  const res = await rpcResilient('get_my_coach_account');
  if (res.error) return res;
  const row = Array.isArray(res.data) ? res.data[0] : null;
  return { data: row ?? null, error: null };
}

export async function createCoachAccount({ displayName } = {}) {
  const res = await rpcResilient('create_coach_account', {
    p_display_name: displayName ?? null,
  });
  if (res.error) return res;
  const row = Array.isArray(res.data) ? res.data[0] : null;
  return { data: row ?? null, error: null };
}

export async function joinCoachByCode(inviteCode) {
  const res = await rpcResilient('join_coach_by_code', {
    p_invite_code: inviteCode ?? null,
  });
  if (res.error) return res;
  const row = Array.isArray(res.data) ? res.data[0] : null;
  if (row && row.linked_coach_id && !row.coach_id) {
    return { data: { coach_id: row.linked_coach_id }, error: null };
  }
  return { data: row ?? null, error: null };
}

export async function listMyCoachMemberships() {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth?.user?.id;
  if (!userId) return { data: null, error: new Error('Not authenticated') };

  const res = await supabase
    .from('coach_memberships')
    .select('id, coach_id, member_id, created_at')
    .eq('member_id', userId)
    .order('created_at', { ascending: false });

  if (res.error || !Array.isArray(res.data) || res.data.length === 0) return res;

  // Fetch coach display names (policy allows linked members to read coach_accounts).
  const coachIds = res.data.map((m) => m.coach_id).filter(Boolean);
  const { data: coaches, error: cErr } = await supabase
    .from('coach_accounts')
    .select('coach_id, display_name')
    .in('coach_id', coachIds);
  if (cErr) return res; // fall back to UUID display

  const byId = new Map((coaches || []).map((c) => [c.coach_id, c.display_name || null]));
  const merged = res.data.map((m) => ({
    ...m,
    coach_display_name: byId.get(m.coach_id) || null,
  }));

  return { ...res, data: merged };
}

export async function leaveCoachMembership(membershipId) {
  return supabase.from('coach_memberships').delete().eq('id', membershipId);
}

export async function removeCoachMember(membershipId) {
  return leaveCoachMembership(membershipId);
}

export async function listMyMembers() {
  const { data: memberships, error: mErr } = await supabase
    .from('coach_memberships')
    .select('id, member_id, created_at')
    .order('created_at', { ascending: false });
  if (mErr) return { data: null, error: mErr };

  const memberIds = (memberships || []).map((m) => m.member_id).filter(Boolean);
  if (!memberIds.length) return { data: [], error: null };

  const [assessRes, programRes] = await Promise.all([
    supabase
      .from('assessments')
      .select('user_id, name, age, gender, height_feet, height_inches, weight_lbs')
      .in('user_id', memberIds),
    supabase
      .from('client_programs')
      .select('user_id, goal, calories, protein_g, carbs_g, fats_g, training_days, created_at')
      .in('user_id', memberIds)
      .order('created_at', { ascending: false }),
  ]);

  const assessments = assessRes?.error ? [] : (assessRes?.data || []);
  const programs = programRes?.error ? [] : (programRes?.data || []);

  const assessByUser = new Map((assessments || []).map((a) => [a.user_id, a]));
  const latestProgramByUser = new Map();
  (programs || []).forEach((p) => {
    if (!latestProgramByUser.has(p.user_id)) latestProgramByUser.set(p.user_id, p);
  });

  const merged = (memberships || []).map((m) => ({
    membershipId: m.id,
    memberId: m.member_id,
    createdAt: m.created_at,
    assessment: assessByUser.get(m.member_id) || null,
    program: latestProgramByUser.get(m.member_id) || null,
  }));

  return { data: merged, error: null };
}

export async function listCoachAlerts({ limit = 50 } = {}) {
  const [checkinsRes, assessmentsRes] = await Promise.all([
    supabase
      .from('weekly_checkins')
      .select('id, userid, createdat, weight, reviewed_at')
      .is('reviewed_at', null)
      .order('createdat', { ascending: false })
      .limit(limit),
    supabase
      .from('assessments')
      .select('user_id, name, weight_lbs, assessment, reviewed_at')
      .is('reviewed_at', null)
      .limit(limit),
  ]);

  if (checkinsRes.error || assessmentsRes.error) {
    return { data: null, error: checkinsRes.error || assessmentsRes.error };
  }

  const assessments = (assessmentsRes.data || []).map((row) => ({
    user_id: row.user_id,
    name: row.name,
    weight_lbs: row.weight_lbs ?? null,
    submitted_at: row.assessment?.created_at || null,
  }));

  return { data: { checkins: checkinsRes.data || [], assessments }, error: null };
}

export async function markAssessmentReviewed(userId) {
  return supabase.rpc('mark_assessment_reviewed', { p_user_id: userId });
}

export async function markWeeklyCheckinReviewed(checkinId) {
  return supabase.rpc('mark_weekly_checkin_reviewed', { p_checkin_id: checkinId });
}
